import { useDomEvent } from 'hooks/use-dom-event'
import { useResizeObserver } from '@repo/studio-hooks-dom/use-resize-observer'
import * as React from 'react'
import ms from 'ms'

const slotMotionMs = ms('250ms')
const passiveScroll = { passive: true }

export function isHeaderMascotSlotOpen(
  entry: { isIntersecting: boolean; boundingClientRect: { height: number } } | null,
) {
  if (!entry || entry.boundingClientRect.height < 1) {
    return false
  }
  return !entry.isIntersecting
}

export function useCollapsingMascot(
  scroller: HTMLElement | null,
  sentinel: HTMLElement | null,
  slot: HTMLElement,
  liftLayer: HTMLElement | null,
  onCollapsed?: React.RefObject<(collapsed: boolean) => void>,
) {
  const [collapsed, setCollapsed] = React.useState(false)
  const scrollerRef = React.useRef(scroller)
  const sentinelRef = React.useRef(sentinel)
  scrollerRef.current = scroller
  sentinelRef.current = sentinel
  const frame = React.useRef(0)

  const publish = React.useCallback(
    (next: boolean) => {
      setCollapsed(current => (current === next ? current : next))
      onCollapsed?.current(next)
    },
    [onCollapsed],
  )

  React.useEffect(() => {
    if (!scroller || !sentinel || typeof IntersectionObserver === 'undefined') {
      publish(false)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        publish(isHeaderMascotSlotOpen(entry ?? null))
      },
      { root: scroller, threshold: 0 },
    )
    observer.observe(sentinel)
    return () => {
      observer.disconnect()
    }
  }, [publish, scroller, sentinel])

  const read = React.useCallback(() => {
    frame.current = 0
    if (!liftLayer || !sentinel || slot.parentElement !== liftLayer) {
      return
    }
    placeOverSentinel(slot, liftLayer, sentinel.getBoundingClientRect())
  }, [liftLayer, sentinel, slot])

  const schedule = React.useCallback(() => {
    if (frame.current !== 0) {
      return
    }
    frame.current = requestAnimationFrame(read)
  }, [read])

  useDomEvent(scrollerRef, 'scroll', scroller && sentinel ? schedule : false, passiveScroll)
  useResizeObserver(scrollerRef, schedule)
  useResizeObserver(sentinelRef, schedule)

  React.useEffect(() => {
    read()
    return () => {
      if (frame.current !== 0) {
        cancelAnimationFrame(frame.current)
        frame.current = 0
      }
    }
  }, [read])

  return collapsed
}

function placeOverSentinel(slot: HTMLElement, layer: HTMLElement, box: DOMRect) {
  const layerBox = layer.getBoundingClientRect()
  slot.style.position = 'absolute'
  slot.style.left = '0'
  slot.style.top = '0'
  slot.style.width = `${box.width}px`
  slot.style.height = `${box.height}px`
  slot.style.transform = `translate3d(${box.left - layerBox.left}px, ${box.top - layerBox.top}px, 0)`
}

function clearMascotLift(slot: HTMLElement) {
  slot.style.position = ''
  slot.style.left = ''
  slot.style.top = ''
  slot.style.width = ''
  slot.style.height = ''
  slot.style.transform = ''
}

interface SettledRect {
  left: number
  top: number
  width: number
  height: number
}

export function useMascotSlotMotion(
  frameRef: React.RefObject<HTMLElement | null>,
  slot: HTMLElement,
  host: HTMLElement | null,
  liftLayer: HTMLElement | null,
  sentinel: HTMLElement | null,
  inHeader: boolean,
  empty: boolean,
) {
  const settledRef = React.useRef<SettledRect | null>(null)
  const headerFlight = React.useRef(inHeader)

  React.useLayoutEffect(() => {
    const crossedHeader = headerFlight.current !== inHeader
    headerFlight.current = inHeader
    const node = frameRef.current
    if (!node || !host) {
      return
    }
    node.style.transition = 'none'
    node.style.transform = 'none'
    const live = node.getBoundingClientRect()
    const previous = settledRef.current
    const moving = slot.parentElement !== host
    if (host !== liftLayer || !sentinel) {
      clearMascotLift(slot)
      slot.className = inHeader ? 'size-8 shrink-0' : 'size-full'
    }
    if (moving) {
      host.appendChild(slot)
    }
    if (host === liftLayer && sentinel) {
      slot.className = ''
      placeOverSentinel(slot, host, sentinel.getBoundingClientRect())
    }
    const before = motionBefore(moving, live, previous)
    const next = node.getBoundingClientRect()
    settledRef.current = {
      left: next.left,
      top: next.top,
      width: next.width,
      height: next.height,
    }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (crossedHeader) {
      if (inHeader && !reducedMotion) {
        node.style.transformOrigin = 'top left'
        node.style.transform = 'translate3d(-100%, 0, 0)'
        node.getBoundingClientRect()
        node.style.transition = `transform ${slotMotionMs}ms cubic-bezier(0.4, 0, 0.2, 1)`
        node.style.transform = 'translate3d(0, 0, 0)'
      }
      return
    }
    if (!before || before.width < 1 || next.width < 1 || next.height < 1) {
      return
    }
    const dx = before.left - next.left
    const dy = before.top - next.top
    const sx = before.width / next.width
    const sy = before.height / next.height
    const unchanged =
      Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(sx - 1) < 0.02 && Math.abs(sy - 1) < 0.02
    const far = Math.abs(dx) > window.innerWidth || Math.abs(dy) > window.innerHeight
    if (unchanged || far || reducedMotion) {
      return
    }
    node.style.transformOrigin = 'top left'
    node.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${sx}, ${sy})`
    node.getBoundingClientRect()
    node.style.transition = `transform ${slotMotionMs}ms cubic-bezier(0.4, 0, 0.2, 1)`
    node.style.transform = 'translate3d(0, 0, 0) scale(1, 1)'
  }, [empty, frameRef, host, inHeader, liftLayer, sentinel, slot])
}

function motionBefore(
  moving: boolean,
  live: DOMRect,
  previous: SettledRect | null,
): SettledRect | null {
  if (!moving) {
    return previous
  }
  if (!previous) {
    return { left: live.left, top: live.top, width: live.width, height: live.height }
  }
  return { left: live.left, top: live.top, width: previous.width, height: previous.height }
}
